import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject } from '@angular/core';

import { catchError, EMPTY, filter, from, map, Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { nanoid } from 'nanoid';

import {
  IILinkedNextDialogData,
  TDialogAnswerPayload,
  ILinkedDialogData,
  EDialogAnswer,
  TDialogData,
} from './dialog.types';

const dialogRefs = new Map<string, NbDialogRef<unknown>>();

/**
 * Фабрика для створення сервісу управління діалогами.
 *
 * Повертає об'єкт з методами для роботи з діалогами:
 * - open: відкриття одиночного діалогу
 * - openLinked: відкриття послідовності пов'язаних діалогів
 * - closeAll: закриття всіх діалогів
 * - refsMap: прямий доступ до Map всіх відкритих діалогів
 *
 * Сервіс автоматично підтримує життєвий цикл діалогів:
 * реєструє їх при відкритті, використовує takeUntilDestroyed для автоматичного завершення
 * та очищує ресурси при закритті компонента.
 *
 * @returns {Object} об'єкт сервісу з методами для управління діалогами
 *
 * @example
 * // Ін'єкція та використання в компоненті
 * const dialogService = dialog();
 *
 * // Відкриття простого діалогу
 * dialogService.open({
 *   component: import('./my-dialog.component'),
 *   isObservable: true
 * }).subscribe(result => { ... });
 *
 * // Відкриття послідовності діалогів
 * dialogService.openLinked({
 *   id: 'dialog-1',
 *   component: import('./dialog1.component'),
 *   next: { id: 'dialog-2', ... }
 * }).subscribe(finalResult => { ... });
 */
export const dialog = () => {
  const linkedDialogs = new Map<string, ILinkedDialogData<any, any>>();
  const dialogService = inject(NbDialogService);
  const destroyRef = inject(DestroyRef);

  /**
   * Відкриває діалог на основі переданої конфігурації.
   *
   * Функція передбачає кілька перевантажень для підтримки різних сценаріїв використання:
   * - isObservable: true - повертає Observable з результатом діалогу
   * - isObservable: false або undefined - негайно підписується та повертає Subscription
   * - linkedDialog - конфіг для послідовних діалогів (див. openLinked)
   *
   * Робота функції:
   * 1. Налаштовує фільтр результатів на основі властивості successOnly (якщо задан)
   * 2. Додає dialogId до контексту компонента для внутрішнього доступу
   * 3. Динамічно завантажує компонент діалогу через from() та switchMap()
   * 4. Відкриває діалог через NbDialogService та зберігає посилання в dialogRefs Map
   * 5. Дозволяє зовнішньому коду стежити за посиланням через refCb callback
   * 6. Обробляє закриття діалогу та очистку ресурсів (видаляє з Map)
   * 7. Автоматично завершує за допомогою takeUntilDestroyed при знищенні компонента
   *
   * @template T - тип результату діалогу
   * @param {Readonly<TDialogData>} data - конфіг діалогу з властивостями:
   *   - component: Import<any> - динамічний імпорт компонента діалогу
   *   - config?: NbDialogConfig - конфіг передачі для Nebular діалогу
   *   - id?: string - унікальний ідентифікатор для відслідковування
   *   - isObservable?: boolean - робить функцію асинхронною (Observable) або синхронною (Subscription)
   *   - successOnly?: boolean - фільтрує результати, повертаючи лише Confirm відповіді
   *   - refCb?: (ref: NbDialogRef | null, id: string) => void - callback для отримання посилання на діалог
   *
   * @returns залежить від перевантаження:
   *   - Observable<TDialogAnswerPayload<T>> коли isObservable: true
   *   - Subscription коли isObservable: false або undefined
   *
   * @example
   * // Отримання Observable з результатом
   * dialogService.open({ component: loadMyDialog(), isObservable: true }).subscribe(result => console.log(result));
   *
   * // Синхронне відкриття (автоматична підписка)
   * dialogService.open({ component: loadMyDialog() });
   */
  function open<T = unknown>(data: Readonly<TDialogData> & { isObservable: true }): Observable<TDialogAnswerPayload<T>>;
  function open<T = unknown>(data: Readonly<TDialogData> & { isObservable?: false }): Subscription;
  function open<T = unknown>(data: Readonly<ILinkedDialogData>): Observable<TDialogAnswerPayload<T>>;
  function open<T = unknown>(data: Readonly<TDialogData>) {
    const filterBySuccessOnly = (result: TDialogAnswerPayload) => {
      if (data.successOnly) {
        if (!result) {
          return false;
        }

        return result.type === EDialogAnswer.Confirm;
      }

      return true;
    };

    // Add dialogId to context if id exists
    if (data.id) {
      (data as TDialogData).config = {
        ...(data.config || {}),
        context: {
          ...(data.config?.context || {}),
          dialogId: data.id,
        },
      };
    }

    const dialogId = data.id || nanoid(10);
    const observable = from(data.component).pipe(
      // Use the map operator to open a dialog with the provided component and options
      switchMap((component) => {
        const dialogRef = dialogService.open(component.default, data.config);

        dialogRefs.set(dialogId, dialogRef);

        if (data.refCb !== undefined) {
          data.refCb(dialogRef, dialogId);
        }

        // Return observable with dialog answer and filter if successOnly flag exist in data
        return dialogRef.onClose.pipe(
          tap(() => {
            dialogRefs.delete(dialogId);

            if (data.refCb !== undefined) {
              data.refCb(null, dialogId);
            }
          }),
          filter((result) => filterBySuccessOnly(result)),
        );
      }),
      catchError((e) => {
        console.log(`%cAn error occurred while loading component:`, 'color: #ff3a5a; font-weight: bold;', e);

        return EMPTY; // Return an empty Observable in case of an error
      }),
      takeUntilDestroyed(destroyRef),
    );

    if (!data.isObservable) {
      return observable.subscribe();
    }

    return observable;
  }

  /**
   * Відкриває послідовність пов'язаних діалогів з можливістю переходу до попередніх та наступних діалогів.
   *
   * Функція управляє ланцюгом діалогів, дозволяючи утворити складні багатокрокові діалогові потоки.
   * Робота функції відбувається в такому порядку:
   *
   * 1. **Ініціалізація**: Зберігає конфігурацію поточного діалогу в linkedDialogs Map
   * 2. **Відкриття діалогу**: Викликає функцію open() з флагом isObservable: true
   * 3. **Трансформація результату**: Якщо задана функція mapDataFn, трансформує результат діалогу
   * 4. **Перевірка переходу**:
   *    - Якщо є data.prev.id, перевіряє можливість переходу до попереднього діалогу
   *      (за умовчанням - коли результат є Cancel)
   *    - Якщо є data.next.id, перевіряє можливість переходу до наступного діалогу
   *      (за умовчанням - коли результат є Confirm)
   *    - Якщо переходу немає, повертає результат
   * 5. **Повторне відкриття**: Якщо умови дозволяють, відкриває наступний/попередній діалог
   *
   * Функція підтримує кастомізовану логіку перехідних умов через canOpenFn та openFn у конфігурації,
   * що дозволяє контролювати поведінку потоку діалогів.
   *
   * @template T - тип початкових даних діалогу
   * @template R - тип результату, що повертається (за замовчуванням T)
   * @template MapFn - тип функції трансформації даних (за замовчуванням T)
   * @template Prev - тип даних для попереднього діалогу (за замовчуванням T)
   * @template Next - тип даних для наступного діалогу (за замовчуванням T)
   *
   * @param {ILinkedDialogData<T, MapFn, Next, Prev>} data - конфігурація пов'язаних діалогів, включаючи:
   *   - id: унікальний ідентифікатор поточного діалогу
   *   - component: компонент діалогу для отримання та відкриття
   *   - config: конфіг передачі для NbDialogService.open()
   *   - mapDataFn?: функція для трансформації результату перед переходом
   *   - prev?: конфіг попереднього діалогу (якщо існує)
   *   - next?: конфіг наступного діалогу (якщо існує)
   *
   * @returns {Observable<TDialogAnswerPayload<R>>} Observable, який емітує остаточний результат
   * після завершення всієї послідовності діалогів. Емітує лише один раз при закритті останнього діалогу.
   *
   * @example
   * // Приклад послідовного діалогу: Підтвердження -> Вибір -> Завершення
   * openLinked({
   *   id: 'confirm-dialog',
   *   component: loadConfirmDialog(),
   *   next: {
   *     id: 'choice-dialog',
   *     openFn: (result) => openChoiceDialog(result)
   *   }
   * })
   */
  function openLinked<T = unknown, R = T, MapFn = T, Prev = T, Next = T>(
    data: ILinkedDialogData<T, MapFn, Next, Prev>,
  ): Observable<TDialogAnswerPayload<R>> {
    linkedDialogs.set(data.id, data);

    // Допоміжна функція для відкриття попереднього/наступного діалогу
    const openNextDialog = (
      { canOpenFn, id, openFn, mapDataFn }: IILinkedNextDialogData<any, any>,
      result: TDialogAnswerPayload,
      defaultAnswerType: EDialogAnswer,
    ) => {
      // Якщо є права, то намагаємося відкрити з параметром функції,
      // інакше зі збереженими даними або повертаємо відповідь діалогу
      const canOpen = canOpenFn ? canOpenFn(result) : result?.type === defaultAnswerType;

      if (!canOpen) {
        return of(result);
      }

      if (openFn) return openFn(result);

      const dialogData = mapDataFn ? mapDataFn(result) : linkedDialogs.get(id) || null;

      if (dialogData) {
        // Записуємо дані діалогу в Map та відкриваємо діалог із цими даними
        linkedDialogs.set(id, dialogData);

        return openLinked(dialogData);
      }

      return of(result);
    };

    return open<T>({ ...data, isObservable: true }).pipe(
      map((result) => {
        // Перетворюємо відповідь, якщо задана функція
        return data.mapDataFn ? data.mapDataFn(result) : result;
      }),
      switchMap((result) => {
        // Пробуємо відкрити попередній діалог.
        if (data?.prev?.id) {
          return openNextDialog(data.prev, result, EDialogAnswer.Cancel);
        }

        // Пробуємо відкрити наступний діалог.
        if (data?.next?.id) {
          return openNextDialog(data.next, result, EDialogAnswer.Confirm);
        }

        // Інакше повертаємо дані
        return of(result);
      }),
    );
  }

  /**
   * Закриває всі відкриті діалоги та очищає внутрішній реєстр.
   *
   * Функція проходить по всім діалогам, які є в Map dialogRefs,
   * закриває їх та видаляє посилання з Map. Це корисно для очистки при знищенні
   * компонента чи при необхідності закрити всі діалоги одночасно (наприклад, при виході з програми).
   *
   * @returns {void}
   *
   * @example
   * // Закрити всі діалоги при навігації
   * router.events.subscribe(() => {
   *   dialog.closeAll();
   * });
   */
  function closeAll() {
    for (const [id, ref] of dialogRefs.entries()) {
      dialogRefs.delete(id);
      ref?.close();
    }
  }

  return { open, openLinked, closeAll, refsMap: dialogRefs };
};
